import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Package, DollarSign } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AppHeader from '../components/AppHeader';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../api/notificationsApi';

const iconForType = {
  new_load: Package,
  new_vehicle: Package,
  payment_status: DollarSign,
};

function Notifications() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const fetchNotifications = async (pageNum = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const result = await getNotifications(pageNum, 15);
      if (result.success) {
        setNotifications((prev) => (append ? [...prev, ...result.notifications] : result.notifications));
        setHasMore(result.has_more);
        setPage(pageNum);
      } else {
        setError(result.message || 'Failed to load notifications.');
      }
    } catch (err) {
      setError('Could not connect to server.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchNotifications(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadMore = () => {
    fetchNotifications(page + 1, true);
  };

  const handleRetry = () => {
    fetchNotifications(1);
  };

  const handleNotificationTap = async (notification) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id_notification === notification.id_notification ? { ...n, is_read: 1 } : n))
    );
    markNotificationRead(notification.id_notification).catch(() => {});

    if (notification.type === 'new_load' || notification.type === 'new_vehicle') {
      navigate(`/loads/${notification.related_id}`);
    } else if (notification.type === 'payment_status') {
      navigate(`/payments/${notification.related_id}`);
    }
  };

  const handleMarkAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
    try {
      await markAllNotificationsRead();
    } catch (err) {
      // Silent fail — worst case it stays marked read locally until next fetch corrects it.
    }
  };

  const hasUnread = notifications.some((n) => !n.is_read);

  return (
   <div style={{ minHeight: '100vh', paddingBottom: 'calc(64px + env(safe-area-inset-bottom))', backgroundColor: '#f9fafb' }}>
      <AppHeader
        title="Notifications"
        showBack={true}
        fallbackRoute="/loads"
        rightAction={
          hasUnread && (
            <button onClick={handleMarkAllRead} style={styles.markAllBtn}>
              Mark all read
            </button>
          )
        }
      />

      <div style={{ padding: 16 }}>
        {loading && <LoadingSpinner fullScreen />}

        {!loading && error && (
          <EmptyState
            icon={Bell}
            title="Something went wrong"
            message={error}
            onRetry={handleRetry}
            retryLabel="Try again"
          />
        )}

        {!loading && !error && notifications.length === 0 && (
          <EmptyState
            icon={Bell}
            title="No notifications"
            message="You're all caught up."
          />
        )}

        {!loading && notifications.map((notification) => {
          const Icon = iconForType[notification.type] || Bell;
          const isUnread = !notification.is_read;

          return (
            <div
              key={notification.id_notification}
              onClick={() => handleNotificationTap(notification)}
              style={{
                ...styles.card,
                backgroundColor: isUnread ? '#eff6ff' : 'white',
              }}
            >
              <div style={styles.iconCircle}>
                <Icon size={20} color="#2563eb" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={styles.notifTitle}>{notification.title}</div>
                <div style={styles.notifMessage}>{notification.message}</div>
                <div style={styles.notifDate}>
                  {notification.created_at.split(' ')[0]}
                </div>
              </div>
              {isUnread && <div style={styles.unreadDot} />}
            </div>
          );
        })}

      {!loading && !error && hasMore && (
  <button onClick={handleLoadMore} disabled={loadingMore} style={styles.loadMoreBtn}>
    {loadingMore ? t('common.loading') : t('common.loadMore')}
  </button>
)}
      </div>
    </div>
  );
}

const styles = {
  markAllBtn: {
    background: 'none',
    border: 'none',
    color: '#2563eb',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
  card: {
    display: 'flex',
    alignItems: 'flex-start',
    borderRadius: 12,
    padding: 14,
    marginBottom: 10,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    cursor: 'pointer',
  },
  iconCircle: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: '#dbeafe',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
    flexShrink: 0,
  },
  notifTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 3,
  },
  notifMessage: {
    fontSize: 13,
    color: '#4b5563',
    marginBottom: 6,
    lineHeight: 1.4,
  },
  notifDate: {
    fontSize: 11,
    color: '#9ca3af',
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#2563eb',
    marginLeft: 8,
    marginTop: 6,
    flexShrink: 0,
  },
  loadMoreBtn: {
    width: '100%',
    padding: '12px 0',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    color: '#2563eb',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    marginTop: 8,
  },
};

export default Notifications;